/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { createHandlerChainRunner, Handler } from "@insite/client-framework/HandlerCreator";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import { getDataLayer, trackAsImpersonation } from "@stoneage/App/Utilities/DataLayer";

type Parameter = {
    filterType:
        | "OnlyShowMyProducts"
        | "PageNumber"
        | "PageSize"
        | "SortType"
        | "Application"
        | "DocumentTypes"
        | "ProductLines"
        | "OnlyShowActiveDocuments"
        | "OnlyShowDiscontinuedDocuments"
        | "Query";
    value: string | number | boolean;
};

type HandlerType = Handler<Parameter, {}>;

export const ToggleByPurchaseFilter: HandlerType = props => {
    if (props.parameter.filterType === "OnlyShowMyProducts") {
        props.dispatch({
            type: "Pages/ProductInformation/AddFilter",
            name: props.parameter.filterType,
            value: props.parameter.value,
        } as any);
    }
};

export const ToggleByActiveFilter: HandlerType = props => {
    if (props.parameter.filterType === "OnlyShowActiveDocuments") {
        props.dispatch({
            type: "Pages/ProductInformation/AddFilter",
            name: props.parameter.filterType,
            value: props.parameter.value,
        } as any);
    }
};

export const ToggleByDiscontinuedFilter: HandlerType = props => {
    if (props.parameter.filterType === "OnlyShowDiscontinuedDocuments") {
        props.dispatch({
            type: "Pages/ProductInformation/AddFilter",
            name: props.parameter.filterType,
            value: props.parameter.value,
        } as any);
    }
};

export const UpdatePageNumberFilter: HandlerType = props => {
    if (props.parameter.filterType === "PageNumber") {
        props.dispatch({
            type: "Pages/ProductInformation/AddFilter",
            name: props.parameter.filterType,
            value: props.parameter.value,
        } as any);
    }
};

export const UpdatePageSizeFilter: HandlerType = props => {
    if (props.parameter.filterType === "PageSize") {
        props.dispatch({
            type: "Pages/ProductInformation/AddFilter",
            name: props.parameter.filterType,
            value: props.parameter.value,
        } as any);
    }
};

export const UpdateSortTypeFilter: HandlerType = props => {
    if (props.parameter.filterType === "SortType") {
        props.dispatch({
            type: "Pages/ProductInformation/AddFilter",
            name: props.parameter.filterType,
            value: props.parameter.value,
        } as any);
    }
};

export const AddApplicationsFilter: HandlerType = props => {
    const applicationFilters = (props.getState() as CustomAppState).xProductInfo.filters.applications;
    const { filterType, value } = props.parameter;
    if (filterType === "Application" && applicationFilters.indexOf(value as string) === -1) {
        props.dispatch({
            type: "Pages/ProductInformation/AddFilter",
            name: filterType,
            value,
        } as any);
    }
};

export const AddDocumentTypesFilter: HandlerType = props => {
    const documentTypeFilters = (props.getState() as CustomAppState).xProductInfo.filters.documentTypes;
    const { filterType, value } = props.parameter;
    if (filterType === "DocumentTypes" && documentTypeFilters.indexOf(value as string) === -1) {
        props.dispatch({
            type: "Pages/ProductInformation/AddFilter",
            name: filterType,
            value,
        } as any);
    }
};

export const AddProductLinesFilter: HandlerType = props => {
    const productLinesFilters = (props.getState() as CustomAppState).xProductInfo.filters.productLines;
    const { filterType, value } = props.parameter;
    if (props.parameter.filterType === "ProductLines" && productLinesFilters.indexOf(value as string) === -1) {
        props.dispatch({
            type: "Pages/ProductInformation/AddFilter",
            name: filterType,
            value,
        } as any);
    }
};

export const AddDocumentSearchQuery: HandlerType = props => {
    const { filterType, value } = props.parameter;

    props.dispatch({
        type: "Pages/ProductInformation/AddFilter",
        name: filterType,
        value,
    } as any);
};

export const TrackGaResourceCenterFilteringEvent: HandlerType = props => {
    const { filterType, value: filterValue } = props.parameter;

    if (filterType === "Query") {
        return;
    }

    let filterName = filterValue;
    if (typeof filterValue === "boolean") {
        if (filterValue) {
            filterName = filterType;
        } else {
            return;
        }
    }

    const state = props.getState();
    const dataLayer = getDataLayer();
    if (!dataLayer) {
        return;
    }

    const billToState = getBillToState(state, state.context.session?.billToId);
    const customerNumber = billToState?.value?.customerNumber;

    const googleAnalyticsData = {
        event: "resourcecenter_filtering",
        text: filterName,
        affiliation: customerNumber || "",
        impersonation: trackAsImpersonation(state),
    };

    dataLayer.push(googleAnalyticsData);
};

export const chain = [
    ToggleByPurchaseFilter,
    ToggleByActiveFilter,
    ToggleByDiscontinuedFilter,
    UpdatePageNumberFilter,
    UpdatePageSizeFilter,
    UpdateSortTypeFilter,
    AddApplicationsFilter,
    AddDocumentTypesFilter,
    AddProductLinesFilter,
    AddDocumentSearchQuery,
    TrackGaResourceCenterFilteringEvent,
];

const addProductInformationFilter = createHandlerChainRunner(chain, "AddProductInformationFilter");
export default addProductInformationFilter;
